/**
 * ONE DOSE-LEVEL OPTION SET PER STUDY (Jim, 2026-10-17).
 *
 * Every form that asks the assigned or administered dose level (DLT, enrolment, administration, end of treatment,
 * serious adverse event...) and the data dictionary must offer the SAME options with the SAME codes. Before this, one form
 * listed the per-cycle figures and another the per-fraction figures of the same levels.
 *
 * STRICT FULL-MATCH PARSING ONLY (Jim, 2026-10-17: free-text parsing made wrong sets; every doubt falls to "no set").
 * A set is built only from text written as a list of levels, and EVERY level segment must match the template exactly:
 *   1. ONE intervention's dose text, split on ";" or a line break. Each segment that names a level must be
 *        Dose Level <-?n>[ (if needed)]: <number> <unit>[ per cycle | full cycle [administered activity] | per fraction]
 *        [ (<number> <unit> per <cycle|full cycle|fraction|dose>[ for N kg adult], ...)]
 *      Segments that name no level (a note about total mass dose) are not level segments and are ignored.
 *      A figure in another unit for the same scale (mCi next to MBq/kg) is an equivalent and is ignored; two figures in the
 *      same unit for one scale is not allowed. Two interventions that both list levels give no set.
 *   2. otherwise the study arms, only when every arm that names a level is written "[Part X ]Dose Level <n> (<number> <unit>)"
 *      and the doses are pairwise different.
 * Anything else (alternatives, ranges, "up to", prose, schedules such as q3w or "x 3 days", a dose-change rule) gives null.
 * When the protocol gives both a per-cycle and a per-fraction figure, every option shows both; when it gives one, only that
 * one; the other is never worked out. Levels are ordered by their number (-1 first) and the code is the position in that
 * order, so the same study gives the same codes on every form. Fewer than two levels is not a set: null.
 *
 * Pure. No imports from the rest of crf/ at run time (crfProtocolReading imports this file).
 */
import type { ResolvedList, AuthOption } from "./crfProtocolReading";
import { studyInterventions } from "../studyProfile/studyProducts";

type Kind = "fraction" | "cycle" | "plain";
interface Quantity { text: string; kind: Kind; unit: string; /** the words after the figure as the protocol wrote them: "per fraction", "per fraction for 80kg adult" */ suffix: string }

const UNIT = "(?:mg\\/kg|mg\\/m(?:2|\\u00b2)|[\\u00b5\\u03bc]g\\/kg|mcg\\/kg|ug\\/kg|[\\u00b5\\u03bc]g\\/m(?:2|\\u00b2)|MBq\\/kg|mCi\\/kg|cells\\/kg|cells\\/m(?:2|\\u00b2)|mg|[\\u00b5\\u03bc]g|mcg|ug|g|MBq|GBq|mCi|Gy|mL|IU)";
const NUM = "\\d+(?:\\.\\d+)?(?:\\s*[x\\u00d7]\\s*10\\s*\\^?\\s*\\d+)?";
const Q = `(${NUM})\\s*(${UNIT})`;
const LEVEL_TOKEN = /\b(Dose[- ]?Level|DL|Cohort)(\s*)([-–−]?)(\s*)(\d+[A-Za-z]?)\b/gi;
const SEGMENT = new RegExp(`^Dose[- ]?Level\\s*(-?)(\\d+[A-Za-z]?)(?:\\s*\\(if needed\\))?\\s*:\\s*${Q}(?:\\s+(per cycle|full cycle(?: administered activity)?|per fraction|per dose))?(?:\\s*,\\s*${Q}\\s+per\\s+(cycle|full cycle|fraction|dose))?(?:\\s*\\(([^()]*)\\))?\\.?$`, "i");
const ITEM = new RegExp(`^${Q}\\s+per\\s+(cycle|full cycle|fraction|dose)(\\s+for\\s+\\d+\\s*kg\\s+(?:adult|participant|patient))?$`, "i");
const ARM = new RegExp(`^(?:(Part\\s+[A-Za-z0-9]+)\\s*[,:\\-]?\\s*)?Dose[- ]?Level\\s*(-?)(\\d+[A-Za-z]?)\\s*\\(${Q}\\)$`, "i");
const SCHEDULE = /\bq\d*[wdm]\b|\bqd\b|\bbid\b|\bdaily\b|\bweekly\b|\bx\s*\d+\s*(?:days?|weeks?|doses?|cycles?)\b/i;

/** "2" or "0.50" -> "2" / "0.5": the figure as a number reads, without a trailing zero. */
const figure = (n: string): string => (/[x×]/i.test(n) ? n.replace(/\s+/g, " ").trim() : String(parseFloat(n)));

interface Level { part: string; rank: number; id: string; name: string; qs: Quantity[]; mentionsCycle: boolean; order: number }

/** "full cycle [administered activity]" and "cycle" read "per cycle"; every other wording is kept as written. */
const suffixOf = (w: string | undefined, qual = ""): string => {
  const t = (w ?? "").toLowerCase().trim().replace(/^per\s+/, "");
  if (!t) return "";
  return `per ${/cycle/.test(t) ? "cycle" : t}${qual}`;
};

const kindOf = (w: string | undefined): Kind => {
  const t = (w ?? "").toLowerCase();
  return /cycle/.test(t) ? "cycle" : /fraction/.test(t) ? "fraction" : "plain";
};

/** Text split on ";" or a line break outside brackets. */
function splitSegments(text: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = "";
  for (const ch of text) {
    if (ch === "(" || ch === "[") depth++;
    else if ((ch === ")" || ch === "]") && depth > 0) depth--;
    if ((ch === ";" || ch === "\n") && depth === 0) { out.push(cur); cur = ""; continue; }
    cur += ch;
  }
  out.push(cur);
  return out.map((x) => x.trim()).filter(Boolean);
}

const namesLevel = (text: string): boolean => new RegExp(LEVEL_TOKEN.source, "i").test(text);
const rankOf = (neg: string, num: string) => (neg ? -1 : 1) * (parseInt(num, 10) + (/[A-Za-z]$/.test(num) ? 0.1 : 0));
const nameOf = (neg: string, num: string) => `Dose Level ${neg ? "-" : ""}${num.toUpperCase()}`;

/** One level segment matched against the template in full, or null. */
function parseSegment(seg: string, order: number): Level | null {
  if (SCHEDULE.test(seg)) return null;
  const m = SEGMENT.exec(seg);
  if (!m) return null;
  const [, neg, num, n1, u1, pword, n2, u2, w2, paren] = m;
  const primaryKind = kindOf(pword);
  const primary: Quantity = { text: `${figure(n1)} ${u1}`, kind: primaryKind, unit: u1.toLowerCase(), suffix: suffixOf(pword) };
  const items: Quantity[] = [primary];
  if (n2 !== undefined) items.push({ text: `${figure(n2)} ${u2}`, kind: kindOf(w2), unit: u2.toLowerCase(), suffix: suffixOf(w2) });
  if (paren !== undefined) {
    for (const piece of paren.split(/\s*,\s*|\s+\/\s+/)) {
      const im = ITEM.exec(piece.trim());
      if (!im) return null;
      items.push({ text: `${figure(im[1])} ${im[2]}`, kind: kindOf(im[3]), unit: im[2].toLowerCase(), suffix: suffixOf(im[3], im[4] ?? "") });
    }
  }
  // Exactly one figure per scale and unit; a figure in another unit for the same scale is an equivalent and is ignored.
  const seen = new Set<string>();
  for (const q of items) {
    if (q.kind === "plain" && q !== primary) return null;
    const k = `${q.kind}|${q.unit}`;
    if (seen.has(k)) return null;
    seen.add(k);
  }
  const pick = (kind: Kind) => items.find((q) => q.kind === kind && q.unit === primary.unit) ?? items.find((q) => q.kind === kind);
  if (primaryKind === "plain" && items.some((q) => q.kind === "cycle")) return null;
  const total = primaryKind === "plain" ? primary : pick("cycle");
  const fraction = pick("fraction");
  const qs = [...(total ? [total] : []), ...(fraction ? [fraction] : [])];
  return { part: "", rank: rankOf(neg, num), id: nameOf(neg, num), name: nameOf(neg, num), qs, mentionsCycle: !!total && total.kind === "cycle", order };
}

interface Found { levels: Level[]; /** a source exists but is not written strictly enough */ failed: boolean; ambiguous: boolean }

/** Levels written as a list in ONE intervention's dose text. */
function levelsFromInterventions(analysis: Record<string, unknown>): Found {
  const out: Level[] = [];
  let lists = 0;
  let failed = false;
  const list = studyInterventions<any>(analysis);
  for (const iv of list) {
    const dose = typeof (iv as { dose?: unknown })?.dose === "string" ? (iv as { dose: string }).dose : "";
    const levelSegs = splitSegments(dose).filter(namesLevel);
    const tokenCount = (dose.match(new RegExp(LEVEL_TOKEN.source, "gi")) ?? []).length;
    if (tokenCount < 1) continue;
    lists++;
    const parsed = levelSegs.map((s, i) => parseSegment(s, out.length + i));
    if (parsed.some((p) => p === null) || parsed.length < 2) { failed = true; continue; }
    out.push(...(parsed as Level[]));
  }
  return { levels: failed ? [] : out, failed, ambiguous: lists > 1 };
}

/** Levels named by the study arms, each arm that names a level written strictly. */
function levelsFromArms(analysis: Record<string, unknown>): Found {
  const out: Level[] = [];
  const arms = Array.isArray(analysis.arms) ? analysis.arms : [];
  for (const arm of arms) {
    const name = typeof (arm as { name?: unknown })?.name === "string" ? (arm as { name: string }).name.trim() : "";
    if (!namesLevel(name)) continue;
    const m = ARM.exec(name);
    if (!m || SCHEDULE.test(name)) return { levels: [], failed: true, ambiguous: false };
    const [, part, neg, num, n1, u1] = m;
    const q: Quantity = { text: `${figure(n1)} ${u1}`, kind: "plain", unit: u1.toLowerCase(), suffix: "" };
    out.push({ part: part ? part.replace(/\s+/g, " ").replace(/^part/i, "Part") : "", rank: rankOf(neg, num), id: nameOf(neg, num), name: nameOf(neg, num), qs: [q], mentionsCycle: false, order: out.length });
  }
  // Pairwise different doses (within a part), and one arm per level.
  const seenDose = new Set<string>();
  const seenLevel = new Set<string>();
  for (const l of out) {
    const dk = `${l.part}|${l.qs[0].text.toLowerCase()}`;
    const lk = `${l.part}|${l.id}`;
    if (seenDose.has(dk) || seenLevel.has(lk)) return { levels: [], failed: true, ambiguous: false };
    seenDose.add(dk);
    seenLevel.add(lk);
  }
  return { levels: out, failed: false, ambiguous: false };
}

const withSuffix = (q: Quantity) => (q.suffix ? `${q.text} ${q.suffix}` : q.text);

/** The figures as the protocol wrote them: nothing added ("total"), nothing dropped ("per dose", "for 80kg adult"). */
function figuresOf(l: Level): string {
  const fraction = l.qs.find((q) => q.kind === "fraction");
  const total = l.qs.find((q) => q.kind !== "fraction");
  if (total && fraction) return `${withSuffix(total)} (${withSuffix(fraction)})`;
  if (total) return withSuffix(total);
  if (fraction) return withSuffix(fraction);
  return "";
}

function labelOf(l: Level): string {
  const prefix = l.part ? `${l.part}, ` : "";
  const f = figuresOf(l);
  return f ? `${prefix}${l.name}: ${f}` : `${prefix}${l.name}`;
}

/** The study's dose-level options, or null when the protocol facts do not state at least two levels strictly. */
export function buildDoseLevels(analysis: Record<string, unknown> | null | undefined): ResolvedList | null {
  if (!analysis || typeof analysis !== "object") return null;
  const iv = levelsFromInterventions(analysis);
  // Unsure means no set: two interventions that both list levels cannot be merged.
  if (iv.ambiguous || iv.failed) return null;
  let levels = iv.levels;
  let from = "interventions";
  if (new Set(levels.map((l) => l.id)).size < 2) {
    const arms = levelsFromArms(analysis);
    if (arms.failed) return null;
    levels = arms.levels;
    from = "study arms";
  }
  if (new Set(levels.map((l) => `${l.part}|${l.id}`)).size < 2) return null;
  const sorted = [...levels].sort((a, b) => a.part.localeCompare(b.part) || a.rank - b.rank || a.order - b.order);
  const keys = new Set(sorted.map((l) => `${l.part}|${l.id}`));
  if (keys.size !== sorted.length) return null; // one level named twice
  // Two levels with the same figures are not two doses.
  if (new Set(sorted.map((l) => `${l.part}|${figuresOf(l).toLowerCase()}`)).size !== sorted.length) return null;
  const options: AuthOption[] = sorted.map((l, i) => ({ code: i + 1, label: labelOf(l) }));
  return { options, layer: "protocol", source: `protocol dose levels (${from}); one set for every form` };
}

/** The variable CODE of a dose-level question: DOSELVL, DOSELEV, EXDOSELV, DLVL, EOTDL, EOTDLI, ENRDL, ADMNDL... */
const DOSE_LEVEL_CODE = /DOSE?LV|DOSE?LEV|DLVL|DLLVL|DLEVEL|DOSE?COH|DL[I1]?$/;
/** Wording that makes it a question ABOUT the level (why, held, concomitant...), whatever else the text says. */
const NOT_THE_LEVEL = /reason|deviat|\bheld\b|\bhold\b|concomitant|medication|reduc|modif|chang|adjust|escalat|\bnext\b|\bprevious\b|\bprior\b|\blimiting\b|\bfrequency\b|\bfirst\b|\bposition\b|\bamong\b|\bwithin\b|\bat this\b|\bnumber of\b|\bdate\b|\bcompleted\b|\bmet\b|\bdifferent\b|\breturned\b|\bif\b/;
const LEVEL = "dose[- ](?:level|cohort)";
const KIND = "(?:assigned|administered|planned|current)";
/** The question IS "which dose level": anchored at both ends. */
const ASKS_THE_LEVEL = [
  new RegExp(`^(?:what (?:is|was) )?(?:the )?(?:${KIND} )?${LEVEL}(?: (?:${KIND}|received|at enrol?lment))?(?: for (?:the )?(?:participant|subject|patient))?\\s*[?:.]?$`),
  new RegExp(`^which ${LEVEL} (?:was|is) (?:the )?(?:participant|subject|patient)\\s*(?:assigned|enrolled|treated|dosed)?(?: to| at| on)?\\s*[?:.]?$`),
];

/**
 * A select that asks WHICH dose level or dose cohort the participant was assigned or given. BOTH a dose-level variable code
 * AND a question that is exactly that question; anything else is untouched.
 */
export function isDoseLevelQuestion(name: string, text: string): boolean {
  const t = text.toLowerCase().trim().replace(/^\(?\d+[.)]\s*/, "").replace(/\s+/g, " ");
  if (NOT_THE_LEVEL.test(t)) return false;
  return DOSE_LEVEL_CODE.test(name.toUpperCase().replace(/[^A-Z0-9]/g, "")) && ASKS_THE_LEVEL.some((re) => re.test(t));
}
